import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import moment from "moment";
import momentTz from "moment-timezone";
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from "react-google-recaptcha-v3";
import {
  bookGiftSession,
  getGiftLanguages,
  getGiftCalendar,
  getGiftMediums,
  getGiftSlots,
  isOk,
  messageOf,
  sendGiftVerificationCode,
  validateGiftCode,
} from "../../services/gift";
import { getCountry, getTimezones } from "../../services/uidata";
import PhoneInputField from "../../components/Inputfields/phoneinput";
import GiftAboutYou from "./components/GiftAboutYou";
import { RECAPTCHA_SITE_KEY } from "../../utils/recaptcha";
import StarsRating from "stars-rating";
import GiftCalendar from "./components/GiftCalendar";
import GiftShell, { Check } from "./components/GiftShell";
import "./gift.css";
import "./redeem.css";

const TOKEN_KEY = "vs_gift_redeem_token";
// Guest email the verification code was sent to (survives a page refresh with the token).
const EMAIL_KEY = "vs_gift_redeem_email";
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Whether the guest's email already has an account ("existing" | "new"), and a
// new guest's "About you" answers — both survive a page refresh with the token.
const ACCOUNT_KEY = "vs_gift_redeem_account";
const ABOUT_KEY = "vs_gift_redeem_about";
const EMPTY_ABOUT = { country: "", countryName: "", countryCode: "", timezone: "", languages: [] };

const readAbout = () => {
  try {
    return { ...EMPTY_ABOUT, ...JSON.parse(sessionStorage.getItem(ABOUT_KEY) || "{}") };
  } catch (err) {
    return EMPTY_ABOUT;
  }
};

// Shown from step 2 onwards in the page header. "About you" (country and
// timezone, like guest booking's first step) is asked of every recipient.
const REDEEM_STEPS = [
  { key: "code", label: "Gift Code" },
  { key: "about", label: "About you" },
  { key: "mediums", label: "Medium" },
  { key: "calendar", label: "Date & time" },
  { key: "details", label: "Your details" },
];

// The browser's timezone in VerySoul's list. Browsers may report a legacy name
// (Chrome: "Asia/Calcutta" for "Asia/Kolkata"), so fall back to a zone with the
// same current UTC offset.
const guessTimezone = (all) => {
  const guessed = momentTz.tz.guess();
  const exact = all.find((tz) => tz.name === guessed);
  if (exact) return exact;
  const offset = momentTz.tz(guessed).utcOffset();
  const sameOffset = all.filter((tz) => {
    try {
      return momentTz.tz(tz.name).utcOffset() === offset;
    } catch (err) {
      return false;
    }
  });
  return sameOffset.find((tz) => tz.status === 1) || sameOffset[0] || null;
};

// A logged-in recipient's timezone id from the profile the app keeps in localStorage.
const profileTimezoneId = () => {
  try {
    const user = JSON.parse(localStorage.getItem("userTimezone") || "null");
    const tz = user && user.timezone;
    return tz && typeof tz === "object" ? tz.id : tz || null;
  } catch (err) {
    return null;
  }
};

const maskEmail = (email) => {
  const parts = String(email || "").split("@");
  return parts.length === 2 ? `${parts[0].slice(0, 1)}***@${parts[1]}` : email;
};

const initialsOf = (name) =>
  String(name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join("");

// Star rating (same as the public profile) and completed professional sessions.
function MediumStats({ medium }) {
  const reviews = Number(medium.rating_count || 0);
  const rating = Number(medium.average_rating || 0);
  const sessions = Number(medium.completed_sessions || 0);

  return (
    <div className="vsr-medium-stats">
      {reviews > 0 ? (
        <span className="vsr-rating" aria-label={`Rated ${rating} out of 5 from ${reviews} ${reviews === 1 ? "review" : "reviews"}`}>
          <StarsRating count={5} value={rating} size={18} color1={"#d9d2c7"} color2={"#e0a526"} edit={false} half={true} />
          <span className="vsr-rating-value">{rating.toFixed(1)}</span>
          <span className="vsr-muted">
            ({reviews} {reviews === 1 ? "review" : "reviews"})
          </span>
        </span>
      ) : (
        <span className="vsr-rating-new">New to VerySoul</span>
      )}
      {sessions > 0 && (
        <span className="vsr-muted vsr-sessions">
          {sessions.toLocaleString()} {sessions === 1 ? "session" : "sessions"} completed
        </span>
      )}
    </div>
  );
}

// A required consent checkbox, as on guest booking.
function ConsentCheck({ id, checked, onChange, error, children }) {
  return (
    <div className="vsr-field">
      <label className="vsr-check" htmlFor={id}>
        <input id={id} type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
        <span>{children}</span>
      </label>
      {error && <span className="vsr-error vsr-consent-error">{error}</span>}
    </div>
  );
}

function MediumAvatar({ medium, size }) {
  const name = medium.name || medium.medium_name || "";
  return medium.profile_image ? (
    <img className={`vsr-avatar vsr-avatar-${size}`} src={process.env.REACT_APP_IMAGE_URL + medium.profile_image} alt="" />
  ) : (
    <span className={`vsr-avatar vsr-avatar-${size}`} aria-hidden="true">
      {initialsOf(name)}
    </span>
  );
}

// Six one-digit boxes; typing moves forward, Backspace moves back, pasting
// a whole code fills every box.
function CodeBoxes({ value, onChange, invalid }) {
  const refs = useRef([]);
  const digits = Array.from({ length: 6 }, (_, i) => value[i] || "");

  const setAt = (index, digit) => {
    const next = digits.slice();
    next[index] = digit;
    onChange(next.join(""));
  };

  return (
    <div className="vsr-code-boxes" role="group" aria-label="6-digit verification code">
      {digits.map((digit, index) => (
        <input
          key={index}
          ref={(el) => (refs.current[index] = el)}
          id={index === 0 ? "vsr-code" : undefined}
          className={`vsr-code-box ${invalid ? "is-invalid" : ""}`}
          inputMode="numeric"
          autoComplete={index === 0 ? "one-time-code" : "off"}
          maxLength={1}
          value={digit}
          aria-label={`Digit ${index + 1}`}
          onChange={(e) => {
            const typed = e.target.value.replace(/\D/g, "");
            if (!typed) return setAt(index, "");
            setAt(index, typed.slice(-1));
            if (index < 5 && refs.current[index + 1]) refs.current[index + 1].focus();
          }}
          onKeyDown={(e) => {
            if (e.key === "Backspace" && !digits[index] && index > 0) refs.current[index - 1].focus();
          }}
          onPaste={(e) => {
            const pasted = (e.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, 6);
            if (!pasted) return;
            e.preventDefault();
            onChange(pasted);
            const last = Math.min(pasted.length, 6) - 1;
            if (refs.current[last]) refs.current[last].focus();
          }}
        />
      ))}
    </div>
  );
}

// Recipient flow (public; QR codes land here as /redeem?code=XXXX-XXXX-XXXX):
// code -> choose medium -> choose service, date and time -> your details -> reserved.
// The recipient never sees a dollar amount.
export default function Redeem() {
  return (
    <GoogleReCaptchaProvider reCaptchaKey={RECAPTCHA_SITE_KEY}>
      <RedeemFlow />
    </GoogleReCaptchaProvider>
  );
}

function RedeemFlow() {
  const location = useLocation();
  const { executeRecaptcha } = useGoogleReCaptcha();

  const [step, setStep] = useState("code");
  const [busy, setBusy] = useState(false);
  const [code, setCode] = useState(new URLSearchParams(location.search).get("code") || "");
  const [token, setToken] = useState(sessionStorage.getItem(TOKEN_KEY) || "");

  const [mediums, setMediums] = useState([]);
  const [medium, setMedium] = useState(null);
  const [service, setService] = useState(null);

  const [timezones, setTimezones] = useState([]);
  const [timezone, setTimezone] = useState("");
  const [month, setMonth] = useState(moment().startOf("month"));
  const [counts, setCounts] = useState({});
  const [date, setDate] = useState("");
  const [slots, setSlots] = useState([]);
  const [time, setTime] = useState("");

  const [loggedIn, setLoggedIn] = useState(Boolean(localStorage.getItem("access_token")));
  const [details, setDetails] = useState({
    first_name: "",
    last_name: "",
    email: sessionStorage.getItem(EMAIL_KEY) || "",
    phone: "",
    verification_code: "",
    // New accounts: guest booking's legal and opt-in questions.
    opt_in: "",
    agree: false,
    over18: false,
    // Everyone: the two session checks.
    responsibility: false,
    permission: false,
  });
  const [accountType, setAccountType] = useState(sessionStorage.getItem(ACCOUNT_KEY) || "");
  const [about, setAbout] = useState(readAbout);
  const [countries, setCountries] = useState([]);
  const [languageList, setLanguageList] = useState([]);
  const [languagesLoading, setLanguagesLoading] = useState(false);
  const [showAllMediums, setShowAllMediums] = useState(false);
  // Guests give their email with the Gift Code and the code is emailed straight
  // away, so the details step opens with the code field already showing.
  const [codeSent, setCodeSent] = useState(Boolean(sessionStorage.getItem(EMAIL_KEY)));
  const [editingEmail, setEditingEmail] = useState(false);
  const [errors, setErrors] = useState({});
  const [reservation, setReservation] = useState(null);

  const recaptcha = async (action) => {
    try {
      return executeRecaptcha ? await executeRecaptcha(action) : "";
    } catch (err) {
      return "";
    }
  };

  // The giver cancelled (or closed) the gift: the gentle card, wherever we are.
  const showUnavailable = (res) => {
    if (!(res && res.data && res.data.error === "unavailable")) return false;
    sessionStorage.removeItem(TOKEN_KEY);
    setToken("");
    setStep("unavailable");
    return true;
  };

  // Token expired / gift no longer usable: back to the code step.
  const handleFailure = (res, fallback) => {
    if (showUnavailable(res)) return;
    if (res && res.data && res.data.error === "token") {
      sessionStorage.removeItem(TOKEN_KEY);
      sessionStorage.removeItem(EMAIL_KEY);
      sessionStorage.removeItem(ACCOUNT_KEY);
      setToken("");
      setCodeSent(false);
      setStep("code");
    }
    toast.error(messageOf(res, fallback));
  };

  // Each step starts at the top of the page.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step]);

  // Timezones: detect the browser's, as guest booking does.
  useEffect(() => {
    getTimezones().then((res) => {
      if (!isOk(res)) return;
      const all = res.data.data || [];
      const visible = all.filter((tz) => tz.status === 1);
      const guess = guessTimezone(all);
      const list = guess && guess.status !== 1 ? [...visible, guess] : visible;
      setTimezones(list);
      // Logged in: the profile's timezone; new guest: their "About you" choice; else the browser's.
      const saved = readAbout().timezone;
      const fromProfile = loggedIn ? profileTimezoneId() : null;
      const preferred = [saved, fromProfile].filter((id) => id && all.some((tz) => String(tz.id) === String(id)))[0];
      setTimezone(String(preferred || (guess ? guess.id : list.length ? list[0].id : "")));
      // "About you" starts on the profile's timezone (logged in) or the detected one, as guest booking does.
      const startZone = fromProfile && list.some((tz) => String(tz.id) === String(fromProfile)) ? fromProfile : guess ? guess.id : "";
      setAbout((current) => (current.timezone ? current : { ...current, timezone: startZone ? String(startZone) : "" }));
    });
    getCountry().then((res) => res && res.data && res.data.status && setCountries(res.data.data || []));
    loadLanguages();
  }, []);

  // Languages for "About you" (also retried from there while the list is empty).
  const loadLanguages = () => {
    if (languagesLoading) return;
    setLanguagesLoading(true);
    getGiftLanguages().then((res) => {
      setLanguagesLoading(false);
      const list = isOk(res) && Array.isArray(res.data.data) ? res.data.data : [];
      if (list.length) setLanguageList(list);
    });
  };

  // Came back with a still-valid token (e.g. page refresh): skip the code step.
  useEffect(() => {
    if (token && !new URLSearchParams(location.search).get("code")) {
      // "About you" not finished before the refresh: ask again.
      const aboutDone = about.country && about.timezone && about.languages.length;
      if (aboutDone) {
        loadMediums(token);
      } else {
        setStep("about");
      }
    }
  }, []);

  const isNewAccount = !loggedIn && accountType === "new";

  const loadMediums = (redeemToken, aboutValue = about) => {
    setBusy(true);
    setShowAllMediums(false);
    // Languages from "About you" (everyone); guests also send their email.
    const options = loggedIn ? { languages: aboutValue.languages } : { languages: aboutValue.languages, email: details.email.trim() };
    getGiftMediums(redeemToken, options).then((res) => {
      setBusy(false);
      if (isOk(res)) {
        setMediums(res.data.data);
        setStep("mediums");
      } else {
        handleFailure(res, "We couldn't load the mediums. Please try again.");
      }
    });
  };

  const submitCode = async (e) => {
    e.preventDefault();
    const found = {};
    if (!code.trim()) found.gift_code = "Please enter your Gift Code.";
    if (!loggedIn && !EMAIL_PATTERN.test(details.email.trim())) found.email = "Please enter a valid email address.";
    setErrors(found);
    if (Object.keys(found).length) return;

    setBusy(true);
    const res = await validateGiftCode(code.trim(), await recaptcha("gift_redeem"));
    setBusy(false);
    if (!isOk(res)) {
      if (showUnavailable(res)) return;
      setErrors({ gift_code: messageOf(res, "We couldn't find that Gift Code.") });
      return;
    }
    const redeemToken = res.data.data.token;
    sessionStorage.setItem(TOKEN_KEY, redeemToken);
    setToken(redeemToken);
    // Guests: email the verification code now, while they choose a medium and time.
    if (!loggedIn) {
      const account = await sendCode(redeemToken, true);
      if (!account) return;
    }
    // Everyone: country and timezone next (like guest booking); new accounts also languages.
    setStep("about");
  };

  const finishAbout = (value) => {
    setAbout(value);
    sessionStorage.setItem(ABOUT_KEY, JSON.stringify(value));
    setTimezone(String(value.timezone));
    loadMediums(token, value);
  };

  const chooseMedium = (item) => {
    setMedium(item);
    setService(item.services[0] || null);
    setDate("");
    setTime("");
    setSlots([]);
    setStep("calendar");
  };

  // Month availability whenever medium / service / timezone / month changes.
  useEffect(() => {
    if (step !== "calendar" || !medium || !service || !timezone) return;
    const from = moment.max(month.clone().startOf("month"), moment()).format("YYYY-MM-DD");
    const to = month.clone().endOf("month").format("YYYY-MM-DD");
    setBusy(true);
    getGiftCalendar({ token, medium_id: medium.medium_id, subsession_id: service.subsession_id, from, to, timezone }).then((res) => {
      setBusy(false);
      if (isOk(res)) {
        setCounts(res.data.data || {});
      } else {
        handleFailure(res, "We couldn't load availability.");
      }
    });
  }, [step, medium, service, timezone, month]);

  const chooseDate = (day) => {
    setDate(day);
    setTime("");
    setBusy(true);
    getGiftSlots({ token, medium_id: medium.medium_id, subsession_id: service.subsession_id, date: day, timezone }).then((res) => {
      setBusy(false);
      if (isOk(res)) {
        setSlots(res.data.data.slots || []);
      } else {
        handleFailure(res, "We couldn't load times for that day.");
      }
    });
  };

  const sendCode = async (redeemToken = token, quiet = false) => {
    const email = details.email.trim();
    if (!EMAIL_PATTERN.test(email)) {
      setErrors({ email: "Please enter a valid email address." });
      return false;
    }
    setBusy(true);
    const res = await sendGiftVerificationCode(redeemToken, email, await recaptcha("gift_verify_email"));
    setBusy(false);
    if (isOk(res)) {
      const account = (res.data.data && res.data.data.account) || "new";
      sessionStorage.setItem(EMAIL_KEY, email);
      sessionStorage.setItem(ACCOUNT_KEY, account);
      setAccountType(account);
      setCodeSent(true);
      setEditingEmail(false);
      setErrors({});
      if (!quiet) toast.success(`We sent a new 6-digit code to ${email}.`);
      if (account === "new" && step === "details" && !about.languages.length) {
        // A different email without an account: country, timezone and languages first.
        toast.info("Please tell us a little about you first.");
        setStep("about");
      }
      return account;
    }
    if (res && res.data && res.data.error === "token") {
      handleFailure(res, "Your gift session expired. Please enter your Gift Code again.");
    } else {
      setErrors({ email: messageOf(res, "We couldn't send the code. Please try again.") });
    }
    return false;
  };

  const validateDetails = () => {
    const found = {};
    if (!loggedIn) {
      if (!EMAIL_PATTERN.test(details.email.trim())) found.email = "Please enter a valid email address.";
      if (codeSent && !/^\d{6}$/.test(details.verification_code.trim())) {
        found.verification_code = "Enter the 6-digit code from your email.";
      }
    }
    if (isNewAccount) {
      if (!details.first_name.trim()) found.first_name = "First name is required.";
      if (!details.last_name.trim()) found.last_name = "Last name is required.";
      if (details.phone.replace(/\D/g, "").length < 10) found.phone = "Please enter a valid phone number.";
      if (!details.opt_in) found.opt_in = "Required";
      if (!details.agree) found.agree = "Required";
      if (!details.over18) found.over18 = "Required";
    }
    if (!details.responsibility) found.responsibility = "Required";
    if (!details.permission) found.permission = "Required";
    setErrors(found);
    return Object.keys(found).length === 0;
  };

  const book = async (e) => {
    e.preventDefault();
    const body = {
      token,
      medium_id: medium.medium_id,
      subsession_id: service.subsession_id,
      date,
      time,
      timezone: Number(timezone),
      responsibility: details.responsibility ? 1 : 0,
      permission: details.permission ? 1 : 0,
    };
    if (!validateDetails()) return;
    if (!loggedIn) {
      // First click: email the code and reveal the code field.
      if (!codeSent) {
        await sendCode();
        return;
      }
      Object.assign(body, { email: details.email.trim(), verification_code: details.verification_code.trim() });
      if (isNewAccount) {
        Object.assign(body, {
          first_name: details.first_name.trim(),
          last_name: details.last_name.trim(),
          phone: `+${details.phone.replace(/\D/g, "")}`,
          country: about.countryName,
          languages: about.languages,
          opt_in: details.opt_in === "Yes",
          agree_to_legal_terms_and_conditionss: details.agree ? 1 : 0,
          over18: details.over18 ? 1 : 0,
        });
      }
    }

    setBusy(true);
    const res = await bookGiftSession(body);
    setBusy(false);
    if (isOk(res)) {
      sessionStorage.removeItem(TOKEN_KEY);
      sessionStorage.removeItem(EMAIL_KEY);
      sessionStorage.removeItem(ACCOUNT_KEY);
      sessionStorage.removeItem(ABOUT_KEY);
      setReservation(res.data.data);
      setStep("reserved");
    } else if (loggedIn && res && res.data && res.data.error && res.data.error.email) {
      // Saved login had expired: book as a guest instead.
      setLoggedIn(false);
      toast.info("Please enter your details to continue.");
    } else if (!loggedIn && /verify your email/i.test(messageOf(res, ""))) {
      setErrors({ verification_code: "That code doesn't match or has expired. Check your email or resend the code." });
    } else if (res && res.data && res.data.error && typeof res.data.error === "object") {
      // A question the backend still needs answered.
      setErrors({ form: messageOf(res, "Please check your details.") });
    } else {
      handleFailure(res, "We couldn't book that time. Please choose another.");
    }
  };

  const timezoneName = () => {
    const tz = timezones.find((item) => String(item.id) === String(timezone));
    return tz ? tz.front_name || tz.name : "";
  };


  const serviceLabel = (item) => (item ? `${item.name}${item.duration ? ` — ${item.duration} min` : ""}` : "");

  const changeService = (value) => {
    const item = medium.services.find((s) => String(s.subsession_id) === value);
    if (!item) return;
    setService(item);
    setDate("");
    setTime("");
    setSlots([]);
  };

  const field = (name) => (errors[name] ? "vsr-input is-invalid" : "vsr-input");

  // Language suggestions: mediums are marked matches_language true/false (null
  // when we don't know the recipient's languages); "Show all" lifts the filter.
  const hasLanguageInfo = mediums.some((item) => item.matches_language !== null && item.matches_language !== undefined);
  const shownMediums = hasLanguageInfo && !showAllMediums ? mediums.filter((item) => item.matches_language !== false) : mediums;
  const languageFiltered = hasLanguageInfo && !showAllMediums && shownMediums.length < mediums.length;
  const pickedNames = languageList.filter((lang) => about.languages.indexOf(lang.id) !== -1).map((lang) => lang.name);
  const languageLabel = pickedNames.length ? pickedNames.join(" or ") : "your language";

  return (
    <GiftShell steps={REDEEM_STEPS} step={step} floating={step === "calendar" && Boolean(time)}>
      {step === "unavailable" && (
        <div className="vsr-center">
          <section className="vsr-card vsr-reserved">
            <span className="vsr-icon-badge" aria-hidden="true">
              <svg viewBox="0 0 24 24">
                <rect x="3" y="8" width="18" height="13" rx="2" />
                <path d="M12 8v13M3 12h18M12 8c-1.5-3-5-3.5-5-1.25S9.5 8 12 8zM12 8c1.5-3 5-3.5 5-1.25S14.5 8 12 8z" />
              </svg>
            </span>
            <h1 className="vsr-title">This gift is no longer available</h1>
            <p className="vsr-lead">
              We're sorry — this VerySoul Gift is no longer available. If you were looking forward to a session, you're still welcome to
              explore VerySoul's mediums.
            </p>
            <div className="vsr-actions">
              <a className="vsr-btn" href="https://verysoul.com/">
                Explore VerySoul
              </a>
            </div>
          </section>
        </div>
      )}

      {step === "about" && (
        <GiftAboutYou
          countries={countries}
          timezones={timezones}
          languages={languageList}
          languagesLoading={languagesLoading}
          onReloadLanguages={loadLanguages}
          value={about}
          onChange={setAbout}
          onNext={finishAbout}
          askLanguages={true}
          busy={busy}
        />
      )}

      {step === "code" && (
        <div className="vsr-stack vsr-center">
          <form className="vsr-card vsr-code-card" onSubmit={submitCode} noValidate>
            <span className="vsr-icon-badge" aria-hidden="true">
              <svg viewBox="0 0 24 24">
                <rect x="3" y="8" width="18" height="13" rx="2" />
                <path d="M12 8v13M3 12h18M12 8c-1.5-3-5-3.5-5-1.25S9.5 8 12 8zM12 8c1.5-3 5-3.5 5-1.25S14.5 8 12 8z" />
              </svg>
            </span>
            <p className="vsr-eyebrow">VerySoul Gift</p>
            <h1 className="vsr-title">You've been given a VerySoul Experience</h1>
            <p className="vsr-lead">
              Someone has given you a private session with a VerySoul Verified Professional Medium. Enter your Gift Code to explore the
              mediums available to you.
            </p>

            <div className="vsr-fields">
              <div className="vsr-field">
                <label className="vsr-label" htmlFor="gift-code">
                  Gift Code<i>*</i>
                </label>
                <input
                  id="gift-code"
                  className={`${field("gift_code")} vsr-input-code`}
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  placeholder="XXXX-XXXX-XXXX"
                  autoComplete="off"
                  aria-invalid={Boolean(errors.gift_code)}
                />
                {errors.gift_code && <span className="vsr-error">{errors.gift_code}</span>}
              </div>
              {!loggedIn && (
                <div className="vsr-field">
                  <label className="vsr-label" htmlFor="gift-email">
                    Your email<i>*</i>
                  </label>
                  <input
                    id="gift-email"
                    type="email"
                    className={field("email")}
                    value={details.email}
                    onChange={(e) => setDetails({ ...details, email: e.target.value })}
                    placeholder="you@example.com"
                    autoComplete="email"
                    aria-invalid={Boolean(errors.email)}
                  />
                  {errors.email ? (
                    <span className="vsr-error">{errors.email}</span>
                  ) : (
                    <span className="vsr-hint">We'll email you a 6-digit code to confirm your booking.</span>
                  )}
                </div>
              )}
              <button type="submit" className="vsr-btn vsr-btn-block" disabled={busy}>
                {busy ? "Checking…" : "Choose My Medium"}
              </button>
            </div>
          </form>

          <ol className="vsr-how">
            <li className="vsr-card">
              <span className="vsr-how-num">1</span>
              <div>
                <p className="vsr-how-title">Choose your medium</p>
                <p className="vsr-muted">Browse the mediums your gift covers.</p>
              </div>
            </li>
            <li className="vsr-card">
              <span className="vsr-how-num">2</span>
              <div>
                <p className="vsr-how-title">Pick a time</p>
                <p className="vsr-muted">Choose a date and time that suits you.</p>
              </div>
            </li>
            <li className="vsr-card">
              <span className="vsr-how-num">3</span>
              <div>
                <p className="vsr-how-title">Nothing to pay</p>
                <p className="vsr-muted">Your gift covers the session. We'll confirm by email.</p>
              </div>
            </li>
          </ol>
        </div>
      )}

      {step === "mediums" && (
        <div className="vsr-stack">
          <div className="vsr-page-head">
            <div>
              <h1 className="vsr-title vsr-title-left">Choose your medium</h1>
              <p className="vsr-lead vsr-lead-left">Take your time — choose the person who feels right for you.</p>
            </div>
            {!loggedIn && codeSent && (
              <p className="vsr-muted">
                Code sent to <b>{maskEmail(details.email)}</b>
              </p>
            )}
          </div>
          {mediums.length === 0 && !busy && (
            <div className="vsr-card vsr-empty">No mediums are available for your gift right now. Please check back soon.</div>
          )}
          {languageFiltered && (
            <div className="vsr-lang-filter">
              {shownMediums.length > 0 ? (
                <span>
                  Showing mediums who speak <b>{languageLabel}</b>.
                </span>
              ) : (
                <span>
                  No mediums who speak <b>{languageLabel}</b> are available right now.
                </span>
              )}
              <button type="button" className="vsr-btn vsr-btn-outline vsr-btn-sm" onClick={() => setShowAllMediums(true)}>
                Show all mediums ({mediums.length})
              </button>
            </div>
          )}
          {showAllMediums && hasLanguageInfo && (
            <div className="vsr-lang-filter">
              <span>Showing all mediums.</span>
              <button type="button" className="vsr-link" onClick={() => setShowAllMediums(false)}>
                Only mediums who speak {languageLabel}
              </button>
            </div>
          )}
          <div className="vsr-medium-grid">
            {shownMediums.map((item) => (
              <article key={item.medium_id} className="vsr-card vsr-medium">
                <MediumAvatar medium={item} size="lg" />
                <h2 className="vsr-medium-name">{item.name}</h2>
                <MediumStats medium={item} />
                {item.languages && <p className="vsr-muted">{item.languages}</p>}
                {item.specialities && item.specialities.length > 0 && (
                  <div className="vsr-chips">
                    {item.specialities.slice(0, 3).map((tag) => (
                      <span key={tag} className="vsr-chip">
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
                <div className="vsr-medium-actions">
                  <button type="button" className="vsr-btn vsr-btn-block vsr-medium-btn" onClick={() => chooseMedium(item)}>
                    Choose {item.name}
                  </button>
                  {/* Public profile in a new tab (gift mode: no paid booking from there). */}
                  <a
                    className="vsr-btn vsr-btn-outline vsr-btn-block vsr-medium-btn"
                    href={`/profile-view/${item.medium_id}?from=gift`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View profile
                  </a>
                </div>
              </article>
            ))}
          </div>
        </div>
      )}

      {step === "calendar" && medium && (
        <div className="vsr-stack">
          <section className="vsr-card vsr-booking-head">
            <div className="vsr-booking-medium">
              <MediumAvatar medium={medium} size="md" />
              <div>
                <button type="button" className="vsr-link" onClick={() => setStep("mediums")}>
                  &larr; Change medium
                </button>
                <h1 className="vsr-booking-title">Book with {medium.name}</h1>
              </div>
            </div>
            <div className="vsr-field vsr-booking-select">
              <label className="vsr-label" htmlFor="gift-session">
                Session
              </label>
              {/* First service is preselected in chooseMedium(). */}
              <select
                id="gift-session"
                className="vsr-select"
                value={service ? String(service.subsession_id) : ""}
                onChange={(e) => changeService(e.target.value)}
              >
                {medium.services.map((item) => (
                  <option key={item.subsession_id} value={String(item.subsession_id)}>
                    {serviceLabel(item)}
                  </option>
                ))}
              </select>
            </div>
            <div className="vsr-field vsr-booking-select">
              <label className="vsr-label" htmlFor="gift-timezone">
                Your timezone
              </label>
              <select id="gift-timezone" className="vsr-select" value={timezone} onChange={(e) => setTimezone(e.target.value)}>
                {timezones.map((tz) => (
                  <option key={tz.id} value={tz.id}>
                    {tz.front_name || tz.name}
                  </option>
                ))}
              </select>
            </div>
          </section>

          <GiftCalendar
            month={month}
            counts={counts}
            selectedDate={date}
            slots={slots}
            selectedTime={time}
            onSelectTime={setTime}
            duration={service ? service.duration : null}
            loading={busy}
            onSelect={chooseDate}
            onMonthChange={(delta) => {
              setMonth(month.clone().add(delta, "month"));
              setDate("");
              setTime("");
              setSlots([]);
            }}
          />

          {time && (
            <div className="vsr-floating-bar" role="region" aria-label="Selected time">
              <div className="vsr-floating-summary">
                <span className="vsr-floating-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24">
                    <rect x="3" y="4" width="18" height="18" rx="2" />
                    <line x1="16" y1="2" x2="16" y2="6" />
                    <line x1="8" y1="2" x2="8" y2="6" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                  </svg>
                </span>
                <div>
                  <p className="vsr-floating-when">
                    {moment(date).format("ddd, MMM D")} at {time}
                  </p>
                  <p className="vsr-muted">
                    {medium.name} · {serviceLabel(service)} · {timezoneName()}
                  </p>
                </div>
              </div>
              <button type="button" className="vsr-btn" onClick={() => setStep("details")}>
                Continue
              </button>
            </div>
          )}
        </div>
      )}

      {step === "details" && medium && service && (
        <div className="vsr-details">
          <form className="vsr-card vsr-details-form" onSubmit={book} noValidate>
            <div>
              <button type="button" className="vsr-link" onClick={() => setStep("calendar")}>
                &larr; Change time
              </button>
              <h1 className="vsr-title vsr-title-left vsr-title-sm">Request your session</h1>
            </div>

            {loggedIn && <p className="vsr-lead vsr-lead-left">You'll book this session with your VerySoul account.</p>}

            {!loggedIn && codeSent && (
              <div className="vsr-code-panel">
                <svg className="vsr-code-panel-icon" viewBox="0 0 24 24" aria-hidden="true">
                  <rect x="2" y="4" width="20" height="16" rx="2" />
                  <polyline points="22 6 12 13 2 6" />
                </svg>
                <div className="vsr-code-panel-body">
                  <p className="vsr-code-panel-title">We've emailed a 6-digit code to {maskEmail(details.email)}</p>
                  <p className="vsr-code-panel-text">Enter it below to confirm it's you.</p>
                  <label className="vsr-label" htmlFor="vsr-code">
                    Verification code<i>*</i>
                  </label>
                  <CodeBoxes
                    value={details.verification_code}
                    invalid={Boolean(errors.verification_code)}
                    onChange={(value) => setDetails({ ...details, verification_code: value })}
                  />
                  {errors.verification_code && <span className="vsr-error">{errors.verification_code}</span>}
                  <p className="vsr-code-panel-text vsr-code-panel-resend">
                    Didn't get it? Check your spam folder or{" "}
                    <button type="button" className="vsr-link vsr-link-inline" disabled={busy} onClick={() => sendCode()}>
                      resend code
                    </button>
                  </p>
                </div>
              </div>
            )}

            {!loggedIn && (
              <div className="vsr-form-grid">
                {isNewAccount && (
                  <>
                    <div className="vsr-field">
                      <label className="vsr-label" htmlFor="gift-first-name">
                        First name<i>*</i>
                      </label>
                      <input
                        id="gift-first-name"
                        className={field("first_name")}
                        placeholder="First name"
                        autoComplete="given-name"
                        value={details.first_name}
                        onChange={(e) => setDetails({ ...details, first_name: e.target.value })}
                      />
                      {errors.first_name && <span className="vsr-error">{errors.first_name}</span>}
                    </div>
                    <div className="vsr-field">
                      <label className="vsr-label" htmlFor="gift-last-name">
                        Last name<i>*</i>
                      </label>
                      <input
                        id="gift-last-name"
                        className={field("last_name")}
                        placeholder="Last name"
                        autoComplete="family-name"
                        value={details.last_name}
                        onChange={(e) => setDetails({ ...details, last_name: e.target.value })}
                      />
                      {errors.last_name && <span className="vsr-error">{errors.last_name}</span>}
                    </div>
                  </>
                )}
                <div className="vsr-field">
                  <label className="vsr-label" htmlFor="gift-details-email">
                    Email<i>*</i>
                  </label>
                  <input
                    id="gift-details-email"
                    type="email"
                    className={field("email")}
                    placeholder="Email"
                    autoComplete="email"
                    value={details.email}
                    readOnly={codeSent && !editingEmail}
                    onChange={(e) => {
                      setDetails({ ...details, email: e.target.value, verification_code: "" });
                      setCodeSent(false);
                    }}
                  />
                  {errors.email && <span className="vsr-error">{errors.email}</span>}
                  {codeSent && !editingEmail && (
                    <button type="button" className="vsr-link vsr-link-small" onClick={() => setEditingEmail(true)}>
                      Use a different email
                    </button>
                  )}
                  {!codeSent && !errors.email && <span className="vsr-hint">We'll email a 6-digit code to this address.</span>}
                  {!isNewAccount && codeSent && !editingEmail && (
                    <span className="vsr-hint">We found your VerySoul account — no other details needed.</span>
                  )}
                </div>
                {isNewAccount && (
                  <div className="vsr-field">
                    <label className="vsr-label" htmlFor="gift-phone">
                      Phone<i>*</i>
                    </label>
                    <PhoneInputField
                      id="gift-phone"
                      name="phone"
                      country={(about.countryCode || "us").toLowerCase()}
                      className={`vsr-phone ${errors.phone ? "is-invalid" : ""}`}
                      value={details.phone}
                      setValue={(value) => setDetails({ ...details, phone: value })}
                    />
                    {errors.phone && <span className="vsr-error">{errors.phone}</span>}
                  </div>
                )}
              </div>
            )}

            {isNewAccount && (
              <div className="vsr-consent">
                <fieldset className="vsr-field vsr-optin">
                  <legend className="vsr-label">
                    Would you like to receive updates and offers from VerySoul?<i>*</i>
                  </legend>
                  <div className="vsr-optin-choices">
                    {["Yes", "No"].map((choice) => (
                      <label key={choice} className={`vsr-optin-choice ${details.opt_in === choice ? "is-selected" : ""}`}>
                        <input
                          type="radio"
                          name="gift-opt-in"
                          value={choice}
                          checked={details.opt_in === choice}
                          onChange={() => setDetails({ ...details, opt_in: choice })}
                        />
                        {choice}
                      </label>
                    ))}
                  </div>
                  {errors.opt_in && <span className="vsr-error">{errors.opt_in}</span>}
                </fieldset>
                <ConsentCheck
                  id="gift-terms"
                  checked={details.agree}
                  error={errors.agree}
                  onChange={(checked) => setDetails({ ...details, agree: checked })}
                >
                  I have read and agree to the{" "}
                  <a href="/terms-conditions" target="_blank" rel="noopener noreferrer">
                    terms and conditions
                  </a>
                  .
                </ConsentCheck>
                <ConsentCheck
                  id="gift-over18"
                  checked={details.over18}
                  error={errors.over18}
                  onChange={(checked) => setDetails({ ...details, over18: checked })}
                >
                  I am over 18.
                </ConsentCheck>
              </div>
            )}

            <div className="vsr-consent">
              <ConsentCheck
                id="gift-responsibility"
                checked={details.responsibility}
                error={errors.responsibility}
                onChange={(checked) => setDetails({ ...details, responsibility: checked })}
              >
                I take sole responsibility for my interpretations, choices, and actions.
              </ConsentCheck>
              <ConsentCheck
                id="gift-permission"
                checked={details.permission}
                error={errors.permission}
                onChange={(checked) => setDetails({ ...details, permission: checked })}
              >
                I will not record without permission from the Medium and understand any recordings are for private use only.
              </ConsentCheck>
            </div>

            {errors.form && (
              <p className="vsr-error vsr-error-block" role="alert">
                {errors.form}
              </p>
            )}

            <button type="submit" className="vsr-btn vsr-btn-block" disabled={busy}>
              {busy ? "Please wait…" : "Request This Session"}
            </button>
          </form>

          <aside className="vsr-card vsr-summary">
            <p className="vsr-eyebrow vsr-eyebrow-left">Your session</p>
            <div className="vsr-summary-medium">
              <MediumAvatar medium={medium} size="md" />
              <div>
                <p className="vsr-summary-name">{medium.name}</p>
                <p className="vsr-muted">{serviceLabel(service)}</p>
              </div>
            </div>
            <dl className="vsr-summary-list">
              <dt>Date</dt>
              <dd>{moment(date).format("dddd, MMMM D, YYYY")}</dd>
              <dt>Time</dt>
              <dd>{time}</dd>
              <dt>Timezone</dt>
              <dd>{timezoneName()}</dd>
              <dt>Cost to you</dt>
              <dd className="vsr-covered">Covered by your gift</dd>
            </dl>
          </aside>
        </div>
      )}

      {step === "reserved" && reservation && (
        <div className="vsr-center">
          <section className="vsr-card vsr-reserved">
            <span className="vsr-icon-badge vsr-icon-badge-success" aria-hidden="true">
              <Check />
            </span>
            <h1 className="vsr-title">We've received your session request</h1>
            <p className="vsr-lead">We've reserved this appointment while we complete your gift.</p>

            <div className="vsr-reserved-session">
              <MediumAvatar medium={medium || { name: reservation.medium_name }} size="md" />
              <div>
                <p className="vsr-summary-name">{reservation.medium_name}</p>
                {reservation.service_name && (
                  <p className="vsr-muted">
                    {reservation.service_name}
                    {reservation.duration ? ` — ${reservation.duration} min` : ""}
                  </p>
                )}
                <p className="vsr-reserved-when">
                  {moment(reservation.date).format("dddd, MMMM D, YYYY")} · {reservation.time}
                  <span className="vsr-reserved-tz">{timezoneName()}</span>
                </p>
              </div>
            </div>

            <ol className="vsr-next">
              <li>
                <span className="vsr-how-num">1</span>
                <span>Your session time is held. And will be confirmed as soon as we finalize payment with the gift giver.</span>
              </li>
              <li>
                <span className="vsr-how-num">2</span>
                <span>
                  We'll email {loggedIn ? "you" : <b>{maskEmail(details.email)}</b>} with session details, including your Zoom link, once everything is finalized.

                </span>
              </li>
              {/* <li>
                <span className="vsr-how-num">3</span>
                <span>Your gift is handled behind the scenes, so your medium will experience this just like any other VerySoul appointment.</span>
              </li> */}
            </ol>

            <a className="vsr-btn vsr-btn-outline" href="https://verysoul.com/">
              Explore VerySoul
            </a>
          </section>
        </div>
      )}
    </GiftShell>
  );
}
